<template>
  <article
    ref="el"
    class="product-item relative flex flex-col flex-grow-1 p-2 sm:p-3 md:p-4 lg:p-3"
  >
    <!-- item is added (show only if added) -->
    <div
      class="absolute top-0 left-0 w-100 h-100 border border-2 border-success rounded-lg events-none"
    />
    <!-- image -->
    <div
      ref="figure"
      class="mixes-catalog__grid-img mb-4 lg:mb-3 text-center"
    >
      <RemoteResponsiveImage
        :product-image-data="ingredient.images[0]?.data"
        img-class="w-100 h-100 image-contain"
        :alt="ingredient.images[0]?.alt || ingredient.name"
        sizes="(min-width: 1200px) 230px, (min-width: 992px) 167px, (min-width: 600px) 183px, (min-width: 500px) 230px, 70vw"
      />
    </div>
    <!-- Discount -->
    <DiscountBubble
      v-if="ingredient.hasDiscount"
      :price-with-discount="parseFloat(ingredient.price)"
      :price-without-discount="parseFloat(ingredient.priceWithoutDiscount)"
      :currency="ingredient.currency"
    />
    <!-- title + description -->
    <h3 class="mb-0 lg:mb-2 text-sm font-medium font-family-base">
      {{ ingredient.name }}
    </h3>
    <p class="mb-2 hidden lg:block text-xs">
      {{ trimRenderedDescription(ingredient.description) }}
    </p>
    <!-- price, grammage, add more -->
    <div class="form-row flex-nowrap mt-auto items-center pt-1 lg:pt-0 leading-sm text-nowrap">
      <div class="col pt-2">
        <div class="row justify-center">
          <!-- price -->
          <div class="col-12">
            <span class="text-sm font-medium">
              {{ numberToCurrency(ingredient.totalPrice, ingredient.currency) }}
            </span>
          </div>
          <div
            v-if="ingredient.hasDiscount"
            class="col-12"
          >
            <span class="line-through text-danger text-sm font-medium">
              {{ numberToCurrency(ingredient.priceWithoutDiscount, ingredient.currency) }}
            </span>
          </div>
          <!-- weight -->
          <div
            v-if="ingredient.parentCategoryId !== 'musli_base' && ingredient.parentCategoryId !== 'porridge_base'"
            class="col-12"
          >
            <span class="text-xs text-muted">{{ ingredient.weight }} g</span>
          </div>
        </div>
      </div>
      <!-- info, add, remove -->
      <div class="col-auto pt-2 static">
        <div class="row no-gutters ml-n1 mt-n1">
          <!-- add -->
          <div class="col-auto pt-1 pl-1 order-1 static">
            <ButtonPlus
              v-if="ingredient.state === 'available'"
              icon-size="16"
              :plus-only="false"
              add-class="px-2"
              @click="addToMix(ingredient)"
            />
            <ButtonState
              v-else
              custom-padding="p-1"
              :product="ingredient"
              :state="ingredient.state"
              icon-size="16"
              :icon-button-only="true"
            />
          </div>
          <!-- info -->
          <div class="col-auto pt-1 pl-1 zindex-2">
            <button
              type="button"
              class="btn btn-outline-warning p-2 stretched-link rounded-pill"
              data-toggle="mixes-detail-modal"
              aria-label="product info"
              @click="showProductDetailModal(ingredient.permalink)"
            >
              <i class="mi-info icon-16 mi-no-mx mi-lh-1" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </article>
</template>

<script setup>
import { noop } from '@vueuse/core'
import { decode } from 'html-entities'
import { striptags } from 'striptags'
import { ref } from 'vue'

import ApiServiceV1 from '@/api/adminApi/AdminApiV1'
import ButtonPlus from '@/components/buttons/ButtonPlus.vue'
import ButtonState from '@/components/buttons/ButtonState.vue'
import DiscountBubble from '@/components/general/BaseDiscountBubble.vue'
import RemoteResponsiveImage from '@/components/general/BaseRemoteResponsiveImage.vue'
import { numberToCurrency } from '@/mixins/numberToCurrency'
import { MixService } from '@/services/mixService'
import { useModalStore } from '@/stores/ModalStore'

const props = defineProps({
  kind: {
    type: String,
    default: 'musli',
  },

  ingredient: {
    type: Object,
    required: true,
    default: noop,
  },
})
const { openModal } = useModalStore()
const el = ref()
const figure = ref()

const addToMix = (ingredient) => {
  const mixService = new MixService(props.kind)
  mixService.addIngredient({ ingredient, quantity: 1 })
  flyIngredient()
}

// HTML text needs to be stripped from HTML then rendered for special chars and then sliced
const trimRenderedDescription = (string) => {
  return `${decode(striptags(string)).slice(0, 70)}...`
}

const flyIngredient = () => {
  // const figure = this.$el.querySelector('figure')
  const ingredientsWrapper =
    window.innerWidth < 992
      ? document.querySelector('.js-component-list-mobile')
      : document.querySelector('.js-component-list-desktop')
  const newFigure = figure.value.cloneNode(true)
  el.value.append(newFigure)
  newFigure.classList.add('component-adding-animation')
  const figureOffset = getOffset(newFigure)
  const tubeOffset = getOffset(ingredientsWrapper)
  const translateX = tubeOffset.left - figureOffset.left + 20
  const translateY = tubeOffset.top - figureOffset.top
  newFigure.style.transform = `translate(${translateX}px, ${translateY}px) scale(0.3)`
  newFigure.style.opacity = 0
  setTimeout(() => {
    newFigure.remove()
  }, 1000)
}

const getOffset = (element) => {
  if (element.getClientRects().length === 0) {
    return { top: 0, left: 0 }
  }
  const rect = element.getBoundingClientRect()
  const win = element.ownerDocument.defaultView
  return {
    top: rect.top + win.pageYOffset,
    left: rect.left + win.pageXOffset,
  }
}

const showProductDetailModal = async (permalink) => {
  const {
    data: { product },
  } = await ApiServiceV1.get(`products/${permalink}`)

  openModal({
    id: 'mixes-detail-modal',
    title: product.name,
    params: {
      product,
    },
  })
}
</script>

<style lang="scss">
.component-adding-animation {
  position: absolute;
  top: 100px;
  left: 100px;
  z-index: 100000000000;
	transition: transform 1s, border-radius 1s, opacity 1s;
	transform-origin: 0 0;
	border-radius: 50%;
}
</style>
